import React, { useState, useRef } from "react"
import { ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"

import { makeStyles } from "tss-react/mui"
import { Button } from "../../../modules/office/admin/components/Button"
import { LocalizedEnum, LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../modules/localization/translations"

const useStyles = makeStyles()(() => ({
    arrowButton: {
        padding: "0 !important",
    },
    buttonGroup: {
        borderRadius: ".5rem",
        ".MuiButtonGroup-grouped:not(:last-of-type)": {
            borderColor: "white",
        },
    },
    buttonFullWidth: {
        ".MuiButtonGroup-grouped:not(:last-of-type)": {
            width: "100%",
        },
    },
}))

type SplitButtonProps = {
    options: string[]
    optionBase: string
    onSelect: (actionKey: string) => void
    buttonLabel: string
    isFullWidth?: boolean
}

export const SplitButton = ({ options, optionBase, onSelect, buttonLabel, isFullWidth = false }: SplitButtonProps) => {
    const [open, setOpen] = useState(false)
    const anchorRef = useRef<HTMLDivElement>(null)
    const { classes, cx } = useStyles()

    const handleMenuItemClick = (actionKey: string) => {
        onSelect(actionKey)
        setOpen(false)
    }

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen)
    }

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return
        }
        setOpen(false)
    }

    return (
        <React.Fragment>
            <ButtonGroup
                variant="contained"
                ref={anchorRef}
                aria-label="Button group with a nested menu"
                className={cx(classes.buttonGroup, { [classes.buttonFullWidth]: isFullWidth })}
            >
                <Button onClick={handleToggle}>{buttonLabel}</Button>
                <Button
                    size="small"
                    className={classes.arrowButton}
                    aria-controls={open ? "split-button-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={handleToggle}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{ zIndex: 10 }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                style={{
                    width: anchorRef.current?.clientWidth,
                }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === "bottom" ? "center top" : "center bottom",
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {options.map((option) => (
                                        <LocalizedEnum base={optionBase} enumValue={option} key={option}>
                                            <MenuItem onClick={() => handleMenuItemClick(option)}>{option}</MenuItem>
                                        </LocalizedEnum>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </React.Fragment>
    )
}
